import { useRef } from 'react';
import { Progress, Flex, Text } from '@mantine/core';
import { DropzoneProps } from '@mantine/dropzone';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import {
	StyledDropzone,
	DashedBox,
	FooterText,
	FooterWrapper,
	FullWidth,
	InnerBox,
	ProgressBarContainer,
	StyledSubText,
	StyledTextButton,
} from './styled';
import { UploadFileData } from './types';
import { useTranslationCommonUi } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { formatBytes } from '@dealstack/frontend/shared/components/MultiFileDropZone/utils';

export const ACCEPT_FILES_TYPES = {
	imagesAndPdf: {
		'image/jpeg': ['.jpeg'],
		'image/png': ['.png'],
		'application/pdf': ['.pdf'],
	},
	pdf: {
		'application/pdf': ['.pdf'],
	},
	excel: {
		'application/vnd.ms-excel': ['.xls'],
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
	},
	word: {
		'application/msword': ['.doc'],
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
	},
};

export interface Props extends Partial<Omit<DropzoneProps, 'onDrop'>> {
	className?: string;
	onDropFile: (file: File[]) => Promise<void>;
	size?: 's' | 'STD';
	description?: string;
	disabled?: boolean;
	icon?: React.ReactNode | null;
	footerText?: string;
	fileInfo?: UploadFileData;
	uploadProgress?: number;
	minWidth?: string;
	height?: string;
	primaryText?: string;
	secondaryText?: string;
}

export const MultiFileDropZone = ({
	className,
	onDropFile,
	description,
	accept,
	icon,
	footerText,
	fileInfo,
	uploadProgress,
	minWidth,
	height,
	primaryText,
	secondaryText,
	...props
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const openRef = useRef<() => void>(null);

	const InnerIcon = (): JSX.Element | null => {
		if (icon === null) {
			return null;
		}
		if (icon) {
			return icon as JSX.Element;
		}
		return <Icon variant="upload-v2" size="xl" color={defaultTheme.v2.color.secondary[800]} />;
	};

	return (
		<StyledDropzone
			$width={minWidth}
			onDrop={onDropFile}
			{...props}
			openRef={openRef}
			activateOnClick={false}
			className={className}
			accept={accept}
		>
			<DashedBox $height={height} onClick={() => openRef?.current?.()} $minWidth={minWidth}>
				<InnerBox>
					<InnerIcon />
					<div>
						{fileInfo?.nbrFiles ? (
							<>
								<Flex direction="row" gap={2}>
									<StyledTextButton>
										{fileInfo?.nbrFiles}{' '}
										{fileInfo?.nbrFiles > 1
											? tCommonUi('multi-file-upload.files')
											: tCommonUi('multi-file-upload.file')}
									</StyledTextButton>
									<StyledSubText>
										{tCommonUi('multi-file-upload.uploaded')}
									</StyledSubText>
								</Flex>
								<StyledSubText>
									{formatBytes(fileInfo?.totalFileSize)}
								</StyledSubText>
								<ProgressBarContainer>
									<FullWidth>
										<Progress value={uploadProgress ?? 0} />
									</FullWidth>
									<Icon
										variant="tick-circle"
										size="lg"
										color={defaultTheme.v2.color.teal[800]}
									/>
								</ProgressBarContainer>
							</>
						) : (
							<Flex direction="column" gap="0.125rem">
								<Flex gap={2}>
									<StyledTextButton>
										{primaryText ?? tCommonUi('multi-file-upload.button')}
									</StyledTextButton>
									<StyledSubText>
										{secondaryText ??
											tCommonUi('multi-file-upload.button-sub-text')}
									</StyledSubText>
								</Flex>
								{!!description && <Text>{description}</Text>}
								{!description && !!accept && (
									<Text>
										{Object.values(accept ?? {})
											.flat()
											.join(', ')}
									</Text>
								)}
							</Flex>
						)}
					</div>
				</InnerBox>
				{footerText && (
					<FooterWrapper>
						<FooterText>{footerText}</FooterText>
					</FooterWrapper>
				)}
			</DashedBox>
		</StyledDropzone>
	);
};
